import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown, faPenToSquare, faRotate } from "@fortawesome/free-solid-svg-icons";
import {
  deleteTeacherCohortAssignment,
  deleteTeacherCohort,
  getCohortPlaygroundProjects,
  getCoursePlanItems,
  getTeacherCohort,
  getTeacherCohortAssignments,
  getTeacherCohortRoster,
  getTeacherLessons,
  patchTeacherCohort,
  patchTeacherCohortAssignment,
  patchTeacherRemoveEnrollment,
  patchTeacherStudentName,
  postTeacherAssignmentCloseShortcut,
  postTeacherCohortEnrollments,
  postTeacherCohortJoinCode,
  postTeacherCohortJoinCodeDisable,
  postTeacherCohortSyncLessonPlans,
  type CohortPlaygroundProjectSummary,
  type CoursePlanItem,
  type TeacherCohortAssignmentRecord,
  type TeacherLessonRecord,
  type TeacherRosterEnrollment,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { parseApiInstant } from "../datetime/parseApiInstant";
import { cohortLessonStudentLinkFromAssignment, copyTextToClipboard } from "../student/cohortLessonStudentLink";
import { useToast } from "../ui/useToast";
import { AddContentModal } from "./AddContentModal";
import { AddLessonModal } from "./AddLessonModal";
import { AddQuizModal } from "./AddQuizModal";
import { AddPresentationModal } from "./AddPresentationModal";
import { AssignmentDueDateModal } from "./AssignmentDueDateModal";
import { CohortLessonPlanRail } from "./CohortLessonPlanRail";
import { EditCohortModal } from "./EditCohortModal";
import { StudentNameModal } from "./StudentNameModal";
import { studentListLabel } from "../people/studentRosterLabel";
import { buildDeleteConcludedCohortConfirmMessage } from "./cohortDeleteMessages";
import { FloatingActionsMenu } from "./FloatingActionsMenu";
import { teacherCohortProgressUrl } from "./submissions/cohortProgressUrl";
import { sortCohortAssignmentsByPlan } from "./cohortAssignmentPlanSort";
import { planItemIdAfterAssignment } from "./quizPlanInsert";

export default function TeacherCohortPage() {
  const { showToast, Toast } = useToast();
  const { cohortId } = useParams<{ cohortId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [courseId, setCourseId] = useState("");
  const [courseTitle, setCourseTitle] = useState("");
  const [cohortStatus, setCohortStatus] = useState<"active" | "archived">("active");
  const [cohortLabel, setCohortLabel] = useState("");
  const [joinCode, setJoinCode] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<TeacherCohortAssignmentRecord[]>([]);
  const [enrollments, setEnrollments] = useState<TeacherRosterEnrollment[]>([]);
  const [total, setTotal] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasLoadedOnce = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [busyJoin, setBusyJoin] = useState(false);
  const [savingTerm, setSavingTerm] = useState(false);
  const [archivingBusy, setArchivingBusy] = useState(false);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [planItems, setPlanItems] = useState<CoursePlanItem[]>([]);
  const [lessons, setLessons] = useState<TeacherLessonRecord[]>([]);
  const [syncBusy, setSyncBusy] = useState(false);
  const [mutAssignmentIds, setMutAssignmentIds] = useState<Set<string>>(() => new Set());
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<Set<string>>(() => new Set());
  const [bulkReleaseBusy, setBulkReleaseBusy] = useState(false);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [playgroundProjects, setPlaygroundProjects] = useState<CohortPlaygroundProjectSummary[]>([]);
  const [dueModalAssignment, setDueModalAssignment] = useState<TeacherCohortAssignmentRecord | null>(null);
  const [dueModalAssignMode, setDueModalAssignMode] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [nameEdit, setNameEdit] = useState<TeacherRosterEnrollment | null>(null);
  const [nameSaving, setNameSaving] = useState(false);
  const [addContentOpen, setAddContentOpen] = useState(false);
  const [addQuizOpen, setAddQuizOpen] = useState(false);
  const [addLessonOpen, setAddLessonOpen] = useState(false);
  const [addPresentationOpen, setAddPresentationOpen] = useState(false);
  const [insertAfterAssignmentId, setInsertAfterAssignmentId] = useState<string | null | undefined>(undefined);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  const cohortWritableForMutations = cohortStatus === "active";

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    if (!bearer || !cohortId) return;
    const silent = opts?.silent ?? hasLoadedOnce.current;
    setError(null);
    if (silent) setRefreshing(true);
    else setInitialLoading(true);
    try {
      const [co, ro, asn, playgroundData] = await Promise.all([
        getTeacherCohort(bearer, cohortId),
        getTeacherCohortRoster(bearer, cohortId, { limit: 200, offset: 0 }),
        getTeacherCohortAssignments(bearer, cohortId),
        getCohortPlaygroundProjects(bearer, cohortId).catch(() => ({ projects: [] as CohortPlaygroundProjectSummary[] })),
      ]);
      setCourseId(co.cohort.courseId);
      setCourseTitle(co.cohort.courseTitle);
      setCohortStatus(co.cohort.status);
      setCohortLabel(co.cohort.label);
      setJoinCode(co.cohort.activeJoinCode);
      setEnrollments(ro.enrollments);
      setTotal(ro.page.total);
      setAssignments(sortCohortAssignmentsByPlan(asn.assignments));
      setPlaygroundProjects(playgroundData.projects);
      hasLoadedOnce.current = true;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load cohort");
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  }, [bearer, cohortId]);

  function markMutating(id: string, busy: boolean) {
    setMutAssignmentIds((prev) => {
      const next = new Set(prev);
      if (busy) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function isMutating(id: string): boolean {
    return mutAssignmentIds.has(id) || bulkReleaseBusy;
  }

  const selectableAssignments = assignments.filter((a) => cohortWritableForMutations && !a.closed);

  const selectedForAssign = [...selectedAssignmentIds].filter((id) => {
    const a = assignments.find((x) => x.id === id);
    return a && !a.closed && !a.released;
  });

  const selectedForUnassign = [...selectedAssignmentIds].filter((id) => {
    const a = assignments.find((x) => x.id === id);
    return a && !a.closed && a.released;
  });

  function toggleAssignmentSelected(id: string) {
    setSelectedAssignmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllAssignments() {
    const eligible = selectableAssignments.map((a) => a.id);
    const allSelected = eligible.length > 0 && eligible.every((id) => selectedAssignmentIds.has(id));
    setSelectedAssignmentIds(allSelected ? new Set() : new Set(eligible));
  }

  async function applyReleasedToAssignments(ids: string[], released: boolean) {
    if (!bearer || !cohortId || !cohortWritableForMutations || ids.length === 0) return;
    setBulkReleaseBusy(true);
    setError(null);
    const snapshots = new Map(assignments.map((a) => [a.id, a]));
    for (const id of ids) markMutating(id, true);
    const failures: string[] = [];
    for (const id of ids) {
      const before = snapshots.get(id);
      if (!before) continue;
      setAssignments((prev) =>
        prev.map((x) =>
          x.id === id
            ? {
                ...x,
                released,
                releasedAt: released ? new Date().toISOString() : null,
                dueAt: released ? x.dueAt : null,
              }
            : x
        )
      );
      try {
        const { assignment } = await patchTeacherCohortAssignment(bearer, cohortId, id, {
          released,
          ...(released ? {} : { dueAt: null }),
        });
        setAssignments((prev) => prev.map((x) => (x.id === id ? assignment : x)));
      } catch (e) {
        setAssignments((prev) => prev.map((x) => (x.id === id ? before : x)));
        failures.push(before.lessonTitle);
        setError(e instanceof Error ? e.message : `Could not update “${before.lessonTitle}”`);
      } finally {
        markMutating(id, false);
      }
    }
    setBulkReleaseBusy(false);
    if (ids.length > 1) setSelectedAssignmentIds(new Set());
    if (failures.length === 0) {
      void refresh({ silent: true });
    }
  }

  async function bulkAssignSelected() {
    await applyReleasedToAssignments(selectedForAssign, true);
  }

  async function bulkUnassignSelected() {
    await applyReleasedToAssignments(selectedForUnassign, false);
  }

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function openAddContentModal(insertAfter: string | null | undefined) {
    setInsertAfterAssignmentId(insertAfter);
    setAddContentOpen(true);
  }

  useEffect(() => {
    const addLesson = searchParams.get("addLesson");
    if (!addLesson || !cohortId) return;
    if (addLesson === "start") openAddContentModal(null);
    else if (addLesson === "end") openAddContentModal(undefined);
    else if (addLesson.startsWith("after-")) openAddContentModal(addLesson.slice("after-".length));
    const next = new URLSearchParams(searchParams);
    next.delete("addLesson");
    setSearchParams(next, { replace: true });
  }, [cohortId, searchParams, setSearchParams]);

  useEffect(() => {
    if (!bearer || !courseId) {
      setLessons([]);
      setPlanItems([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const [lessonData, planData] = await Promise.all([
          getTeacherLessons(bearer, courseId, { limit: 200 }),
          getCoursePlanItems(bearer, courseId),
        ]);
        if (cancelled) return;
        setLessons(lessonData.lessons);
        setPlanItems(planData.items);
      } catch {
        if (!cancelled) {
          setLessons([]);
          setPlanItems([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, courseId]);

  async function handleSyncLessonPlans() {
    if (!bearer || !cohortId || !cohortWritableForMutations) return;
    setSyncBusy(true);
    setError(null);
    try {
      await postTeacherCohortSyncLessonPlans(bearer, cohortId);
      await refresh({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not sync lesson plans");
    } finally {
      setSyncBusy(false);
    }
  }

  async function rotateJoinCode() {
    if (!bearer || !cohortId) return;
    if (!cohortWritableForMutations) return;
    setBusyJoin(true);
    setError(null);
    try {
      await postTeacherCohortJoinCode(bearer, cohortId);
      await refresh({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not create join code");
    } finally {
      setBusyJoin(false);
    }
  }

  async function disableJoinCodes() {
    if (!bearer || !cohortId) return;
    if (!cohortWritableForMutations) return;
    if (!window.confirm("Disable active join codes for this cohort? Students will need a new code.")) return;
    setBusyJoin(true);
    setError(null);
    try {
      await postTeacherCohortJoinCodeDisable(bearer, cohortId);
      await refresh({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not disable codes");
    } finally {
      setBusyJoin(false);
    }
  }

  async function removeEnrollment(eid: string) {
    if (!bearer || !cohortId) return;
    if (!cohortWritableForMutations) return;
    if (!window.confirm("Remove this student from the cohort roster?")) return;
    setError(null);
    try {
      await patchTeacherRemoveEnrollment(bearer, cohortId, eid);
      await refresh({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update enrollment");
    }
  }

  async function handleSaveTerm() {
    if (!bearer || !cohortId) return;
    const trimmed = cohortLabel.trim();
    if (!trimmed) return;
    if (!cohortWritableForMutations) return;
    setSavingTerm(true);
    setError(null);
    try {
      await patchTeacherCohort(bearer, cohortId, { label: trimmed });
      await refresh({ silent: true });
      setEditModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update cohort label");
    } finally {
      setSavingTerm(false);
    }
  }

  async function handleArchiveCohort() {
    if (!bearer || !cohortId) return;
    if (!window.confirm("Archive this cohort for the term? Join codes disable and edits lock until you reactivate.")) return;
    setArchivingBusy(true);
    setError(null);
    try {
      await patchTeacherCohort(bearer, cohortId, { archived: true });
      await refresh({ silent: true });
      setEditModalOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not archive cohort");
    } finally {
      setArchivingBusy(false);
    }
  }

  async function handleReactivateCohort() {
    if (!bearer || !cohortId) return;
    if (!window.confirm("Reactivate this cohort? You can turn join codes back on afterward.")) return;
    setArchivingBusy(true);
    setError(null);
    try {
      await patchTeacherCohort(bearer, cohortId, { archived: false });
      await refresh({ silent: true });
      setEditModalOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not reactivate cohort");
    } finally {
      setArchivingBusy(false);
    }
  }

  async function handleDeleteCohort() {
    if (!bearer || !cohortId) return;
    if (!window.confirm(buildDeleteConcludedCohortConfirmMessage(cohortLabel))) return;
    setDeletingBusy(true);
    setError(null);
    try {
      await deleteTeacherCohort(bearer, cohortId);
      navigate("/teacher/cohorts");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete cohort");
    } finally {
      setDeletingBusy(false);
    }
  }

  async function unassignAssignment(a: TeacherCohortAssignmentRecord) {
    await applyReleasedToAssignments([a.id], false);
  }

  function openDueModal(a: TeacherCohortAssignmentRecord, assignMode = false) {
    setDueModalAssignMode(assignMode);
    setDueModalAssignment(a);
  }

  async function saveDueDate(a: TeacherCohortAssignmentRecord, dueAt: string | null) {
    if (!bearer || !cohortId) return;
    if (!cohortWritableForMutations) return;
    if (!dueModalAssignMode && !a.released) return;
    markMutating(a.id, true);
    setError(null);
    try {
      const patch = dueModalAssignMode ? { dueAt, released: true } : { dueAt };
      const { assignment } = await patchTeacherCohortAssignment(bearer, cohortId, a.id, patch);
      setAssignments((prev) => prev.map((x) => (x.id === a.id ? assignment : x)));
      setDueModalAssignment(assignment);
      void refresh({ silent: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not update due date");
      throw e;
    } finally {
      markMutating(a.id, false);
    }
  }

  async function copyStudentLinkForAssignment(a: TeacherCohortAssignmentRecord) {
    if (!cohortId) return;
    const link = cohortLessonStudentLinkFromAssignment(cohortId, a);
    const ok = await copyTextToClipboard(link);
    if (ok) {
      showToast("Student link copied");
    } else {
      showToast("Could not copy link", "error");
    }
  }

  const dueModalStudentLink =
    dueModalAssignment && cohortId
      ? cohortLessonStudentLinkFromAssignment(cohortId, dueModalAssignment)
      : null;

  async function handleInviteByEmail(e: React.FormEvent) {
    e.preventDefault();
    if (!bearer || !cohortId) return;
    if (!cohortWritableForMutations) return;
    const parts = inviteEmails
      .split(/[\s,;]+/g)
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);
    const uniq = [...new Set(parts)];
    if (uniq.length === 0) {
      setError("Enter one or more student emails separated by commas or newlines.");
      return;
    }
    setInviteBusy(true);
    setError(null);
    try {
      const { results, counts } = await postTeacherCohortEnrollments(bearer, cohortId, { emails: uniq });
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        const lines = failed.map((r) => {
          const reason =
            r.error === "student_not_registered"
              ? "has not signed in yet (share the join code, or ask them to open the app once)"
              : r.error === "account_wrong_role"
                ? "is registered as a teacher/admin, not a student"
                : r.error ?? "failed";
          return `${r.email}: ${reason}`;
        });
        setError(
          counts.succeeded > 0
            ? `Invited ${counts.succeeded}, ${counts.failed} failed.\n${lines.join("\n")}`
            : `No students invited.\n${lines.join("\n")}`
        );
      }
      if (counts.succeeded > 0) {
        setInviteEmails("");
        await refresh({ silent: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invite failed");
    } finally {
      setInviteBusy(false);
    }
  }

  async function toggleAssignmentHidden(a: TeacherCohortAssignmentRecord) {
    if (!bearer || !cohortId || !cohortWritableForMutations || mutAssignmentIds.has(a.id)) return;
    const nextHidden = !a.hidden;
    markMutating(a.id, true);
    setError(null);
    setAssignments((prev) =>
      prev.map((x) =>
        x.id === a.id
          ? {
              ...x,
              hidden: nextHidden,
              hiddenAt: nextHidden ? new Date().toISOString() : null,
            }
          : x
      )
    );
    try {
      const { assignment } = await patchTeacherCohortAssignment(bearer, cohortId, a.id, {
        hidden: nextHidden,
      });
      setAssignments((prev) => prev.map((x) => (x.id === a.id ? assignment : x)));
    } catch (err) {
      setAssignments((prev) => prev.map((x) => (x.id === a.id ? a : x)));
      setError(err instanceof Error ? err.message : "Could not update visibility");
    } finally {
      markMutating(a.id, false);
    }
  }

  async function closeAssignment(a: TeacherCohortAssignmentRecord) {
    if (!bearer || !cohortId || !cohortWritableForMutations) return;
    if (!window.confirm(`Close lesson “${a.lessonTitle}” for student launch? Students will no longer see it.`)) return;
    markMutating(a.id, true);
    setError(null);
    try {
      const { assignment } = await postTeacherAssignmentCloseShortcut(bearer, cohortId, a.id);
      setAssignments((prev) =>
        sortCohortAssignmentsByPlan(prev.map((x) => (x.id === a.id ? assignment : x)))
      );
      void refresh({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not close assignment");
    } finally {
      markMutating(a.id, false);
    }
  }

  async function reopenAssignment(a: TeacherCohortAssignmentRecord) {
    if (!bearer || !cohortId || !cohortWritableForMutations) return;
    markMutating(a.id, true);
    setError(null);
    try {
      const { assignment } = await patchTeacherCohortAssignment(bearer, cohortId, a.id, { closed: false });
      setAssignments((prev) => prev.map((x) => (x.id === a.id ? assignment : x)));
      void refresh({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not reopen assignment");
    } finally {
      markMutating(a.id, false);
    }
  }

  async function deleteAssignment(a: TeacherCohortAssignmentRecord) {
    if (!bearer || !cohortId || !cohortWritableForMutations) return;
    if (!window.confirm(`Remove assignment “${a.lessonTitle}” from this cohort? This deletes linked workspaces.`)) return;
    markMutating(a.id, true);
    setError(null);
    try {
      await deleteTeacherCohortAssignment(bearer, cohortId, a.id);
      setSelectedAssignmentIds((prev) => {
        const next = new Set(prev);
        next.delete(a.id);
        return next;
      });
      setAssignments((prev) => prev.filter((x) => x.id !== a.id));
      void refresh({ silent: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not delete assignment");
    } finally {
      markMutating(a.id, false);
    }
  }

  async function saveStudentName(givenName: string, familyName: string) {
    if (!bearer || !cohortId || !nameEdit) return;
    setNameSaving(true);
    setError(null);
    try {
      const saved = await patchTeacherStudentName(bearer, cohortId, nameEdit.studentUserId, {
        givenName,
        familyName,
      });
      setEnrollments((prev) =>
        prev.map((row) =>
          row.studentUserId === saved.studentUserId
            ? { ...row, givenName: saved.givenName, familyName: saved.familyName }
            : row
        )
      );
      setNameEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save student name");
    } finally {
      setNameSaving(false);
    }
  }

  const active = enrollments.filter((r) => r.status === "active");

  const playgroundCountByStudent = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of playgroundProjects) {
      counts.set(p.ownerUserId, (counts.get(p.ownerUserId) ?? 0) + 1);
    }
    return counts;
  }, [playgroundProjects]);

  const lessonIconById = useMemo(() => {
    const map = new Map<string, TeacherLessonRecord>();
    for (const l of lessons) map.set(l.id, l);
    return map;
  }, [lessons]);

  function lessonIconForAssignment(a: TeacherCohortAssignmentRecord) {
    const fromList = a.lessonId ? lessonIconById.get(a.lessonId) : undefined;
    return {
      iconName: a.iconName ?? fromList?.iconName,
      iconBgColor: a.iconBgColor ?? fromList?.iconBgColor,
      iconFgColor: a.iconFgColor ?? fromList?.iconFgColor,
      workspaceMode: a.workspaceMode ?? fromList?.workspaceMode,
    };
  }

  return (
    <div className="teacher-page">
      <nav className="teacher-breadcrumb">
        <Link to="/teacher/cohorts">Cohorts</Link>
        <span aria-hidden> / </span>
        {courseId ? (
          <>
            <Link to={`/teacher/courses/${courseId}`}>{courseTitle || "…"}</Link>
            <span aria-hidden> / </span>
          </>
        ) : null}
        <span>{cohortLabel || "Cohort"}</span>
      </nav>

      <header className="teacher-page-header teacher-library-header">
        <div>
          <h1 className="teacher-page-title">{cohortLabel || "…"}</h1>
          <p className="teacher-page-lede muted">
            {courseTitle ? `${courseTitle}. ` : null}
            {total} enrollment record(s). Manage join codes, lesson plans, and your student roster.
          </p>
        </div>
        <div className="teacher-library-actions">
          {cohortId ? (
            <Link to={teacherCohortProgressUrl(cohortId)} className="btn secondary">
              View progress
            </Link>
          ) : null}
          <div className="teacher-actions-dropdown">
            <button
              ref={menuTriggerRef}
              type="button"
              className="btn primary"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
            >
              Actions <FontAwesomeIcon icon={faCaretDown} />
            </button>
            <FloatingActionsMenu
              anchorRef={menuTriggerRef}
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
            >
              <button
                type="button"
                role="menuitem"
                className="teacher-actions-menu-item"
                onClick={() => {
                  setMenuOpen(false);
                  setEditModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faPenToSquare} className="teacher-actions-menu-icon" />
                Edit cohort
              </button>
              {cohortWritableForMutations ? (
                <button
                  type="button"
                  role="menuitem"
                  className="teacher-actions-menu-item"
                  disabled={syncBusy || !courseId}
                  onClick={() => {
                    setMenuOpen(false);
                    void handleSyncLessonPlans();
                  }}
                >
                  <FontAwesomeIcon icon={faRotate} className="teacher-actions-menu-icon" />
                  Sync future lesson plans
                </button>
              ) : null}
            </FloatingActionsMenu>
          </div>
        </div>
      </header>

      <section className="teacher-join-hero" aria-labelledby="cohort-join-code-heading">
        <div className="teacher-join-hero-copy">
          <h2 id="cohort-join-code-heading" className="teacher-join-hero-title">
            Join code
          </h2>
          <p className="muted small">Share this code so students can join this cohort.</p>
        </div>
        {initialLoading ? (
          <p className="muted">Loading…</p>
        ) : (
          <>
            <p className="teacher-join-hero-code">
              <code>{joinCode ?? "No active code"}</code>
            </p>
            <div className="teacher-actions-row teacher-join-hero-actions">
              <button
                type="button"
                className="btn primary"
                disabled={busyJoin || !cohortWritableForMutations}
                onClick={() => void rotateJoinCode()}
              >
                {joinCode ? "Rotate code" : "Generate code"}
              </button>
              <button
                type="button"
                className="btn secondary danger"
                disabled={busyJoin || !joinCode || !cohortWritableForMutations}
                onClick={() => void disableJoinCodes()}
              >
                Disable codes
              </button>
            </div>
          </>
        )}
      </section>

      {error ? (
        <div className="warn-banner" role="alert" style={{ whiteSpace: "pre-line" }}>
          {error}
        </div>
      ) : null}

      {cohortStatus === "archived" ? (
        <div className="teacher-callout-muted" role="status">
          <p>This cohort is archived. Join codes stay off and edits are blocked until you reactivate.</p>
        </div>
      ) : null}

      <section className="teacher-panel teacher-panel--flush teacher-panel--overflow-visible">
        <h2 className="teacher-panel-title">
          Cohort lesson plan
          {refreshing ? <span className="muted small"> · Updating…</span> : null}
        </h2>
        <p className="muted small teacher-pad" style={{ marginTop: 0, paddingTop: 0 }}>
          <strong>Lesson plans</strong> are planned cohort work (not yet visible to students).{" "}
          <strong>Assigned lessons</strong> are student-facing snapshots. Template edits do not overwrite them.
          Hover between rows to add a lesson or quiz.
        </p>
        {initialLoading ? (
          <p className="muted teacher-pad">Loading…</p>
        ) : (
          <>
            {cohortWritableForMutations && selectableAssignments.length > 0 ? (
                  <div className="teacher-pad teacher-actions-row" style={{ paddingTop: 0 }}>
                    <button
                      type="button"
                      className="btn primary"
                      disabled={bulkReleaseBusy || selectedForAssign.length === 0}
                      onClick={() => void bulkAssignSelected()}
                    >
                      {bulkReleaseBusy ? "Working…" : `Assign selected (${selectedForAssign.length})`}
                    </button>
                    <button
                      type="button"
                      className="btn secondary"
                      disabled={bulkReleaseBusy || selectedForUnassign.length === 0}
                      onClick={() => void bulkUnassignSelected()}
                    >
                      {bulkReleaseBusy ? "Working…" : `Unassign selected (${selectedForUnassign.length})`}
                    </button>
                    {selectedAssignmentIds.size > 0 ? (
                      <button
                        type="button"
                        className="btn secondary"
                        disabled={bulkReleaseBusy}
                        onClick={() => setSelectedAssignmentIds(new Set())}
                      >
                        Clear selection
                      </button>
                    ) : null}
                  </div>
                ) : null}
              <CohortLessonPlanRail
                assignments={assignments}
                cohortId={cohortId}
                courseId={courseId}
                bearer={bearer}
                writable={cohortWritableForMutations}
                bulkReleaseBusy={bulkReleaseBusy}
                selectedAssignmentIds={selectedAssignmentIds}
                selectableAssignments={selectableAssignments}
                isMutating={isMutating}
                lessonIconForAssignment={lessonIconForAssignment}
                onOrderSaved={() => refresh({ silent: true })}
                onError={setError}
                onToggleSelectAll={toggleSelectAllAssignments}
                onToggleSelected={toggleAssignmentSelected}
                onInsert={openAddContentModal}
                onOpenDueModal={(a) => openDueModal(a, false)}
                onAssign={(a) => openDueModal(a, true)}
                onUnassign={(a) => void unassignAssignment(a)}
                onClose={(a) => void closeAssignment(a)}
                onReopen={(a) => void reopenAssignment(a)}
                onDelete={(a) => void deleteAssignment(a)}
                onCopyStudentLink={(a) => void copyStudentLinkForAssignment(a)}
                onToggleHidden={(a) => void toggleAssignmentHidden(a)}
              />
          </>
        )}
      </section>

      <section className="teacher-panel teacher-panel--flush">
          <h2 className="teacher-panel-title">Students ({active.length} active)</h2>
          {cohortWritableForMutations ? (
            <form className="teacher-pad stack-form tight" onSubmit={(ev) => void handleInviteByEmail(ev)}>
              <label className="field" style={{ marginBottom: 0 }}>
                <span className="field-label">Invite by email</span>
                <textarea
                  className="input"
                  rows={3}
                  value={inviteEmails}
                  onChange={(ev) => setInviteEmails(ev.target.value)}
                  placeholder={"One per line or comma-separated\n(student must have signed in once)"}
                  disabled={inviteBusy}
                />
              </label>
              <button type="submit" className="btn secondary" disabled={inviteBusy}>
                {inviteBusy ? "Inviting…" : "Invite / restore enrollments"}
              </button>
              <p className="muted small" style={{ margin: 0 }}>
                Students must have signed in at least once (student account). New students should use the
                join code instead.
              </p>
            </form>
          ) : (
            <p className="muted small teacher-pad">Email invites disabled while archived.</p>
          )}
          {initialLoading && enrollments.length === 0 ? (
            <p className="muted teacher-pad">Loading…</p>
          ) : enrollments.length === 0 ? (
            <p className="muted teacher-pad">No enrollments yet.</p>
          ) : (
            <div className="teacher-table-wrap">
              <table className="teacher-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Joined</th>
                    <th>Playgrounds</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {enrollments.map((r) => {
                    const playgroundCount = playgroundCountByStudent.get(r.studentUserId) ?? 0;
                    return (
                    <tr key={r.enrollmentId}>
                      <td>{r.email ?? r.googleSub ?? r.studentUserId}</td>
                      <td>
                        <div className="teacher-roster-name">
                          {r.givenName && r.familyName ? (
                            <span>
                              {studentListLabel({
                                email: r.email ?? "",
                                givenName: r.givenName,
                                familyName: r.familyName,
                              })}
                            </span>
                          ) : (
                            <span className="muted">No name yet</span>
                          )}
                          <button
                            type="button"
                            className="btn secondary tight"
                            onClick={() => setNameEdit(r)}
                          >
                            {r.givenName && r.familyName ? "Edit" : "Add name"}
                          </button>
                        </div>
                      </td>
                      <td>{r.status}</td>
                      <td className="muted small">
                        {parseApiInstant(r.createdAt)?.toLocaleString() ?? r.createdAt}
                      </td>
                      <td>
                        {playgroundCount === 0 ? (
                          <button
                            type="button"
                            className="btn secondary tight"
                            disabled
                            title="No Playgrounds"
                          >
                            0 playgrounds
                          </button>
                        ) : (
                          <Link
                            to={`/teacher/cohorts/${cohortId ?? ""}/students/${r.studentUserId}/playgrounds`}
                            className="btn secondary tight"
                            style={{ textDecoration: "none" }}
                          >
                            {playgroundCount} playground{playgroundCount === 1 ? "" : "s"}
                          </Link>
                        )}
                      </td>
                      <td className="teacher-table-actions">
                        {r.status === "active" ? (
                          <button
                            type="button"
                            className="btn secondary danger tight"
                            disabled={!cohortWritableForMutations}
                            onClick={() => void removeEnrollment(r.enrollmentId)}
                          >
                            Remove
                          </button>
                        ) : (
                          <span className="muted small">-</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

      <AssignmentDueDateModal
        open={dueModalAssignment !== null}
        lessonTitle={dueModalAssignment?.lessonTitle ?? ""}
        itemKind={dueModalAssignment?.itemKind}
        dueAt={dueModalAssignment?.dueAt ?? null}
        assignMode={dueModalAssignMode}
        studentLinkUrl={dueModalStudentLink}
        disabled={!cohortWritableForMutations}
        saving={dueModalAssignment ? isMutating(dueModalAssignment.id) : false}
        onClose={() => {
          setDueModalAssignment(null);
          setDueModalAssignMode(false);
        }}
        onCommit={async (dueAt) => {
          if (!dueModalAssignment) return;
          await saveDueDate(dueModalAssignment, dueAt);
        }}
        onCopyToast={showToast}
      />

      <StudentNameModal
        open={nameEdit !== null}
        email={nameEdit?.email ?? ""}
        savedGivenName={nameEdit?.givenName ?? null}
        savedFamilyName={nameEdit?.familyName ?? null}
        saving={nameSaving}
        onClose={() => {
          if (!nameSaving) setNameEdit(null);
        }}
        onSave={saveStudentName}
      />

      <EditCohortModal
        open={editModalOpen}
        cohortLabel={cohortLabel}
        cohortStatus={cohortStatus}
        disabled={cohortStatus === "archived"}
        saving={savingTerm}
        archivingBusy={archivingBusy}
        deletingBusy={deletingBusy}
        onClose={() => setEditModalOpen(false)}
        onLabelChange={setCohortLabel}
        onSaveLabel={() => void handleSaveTerm()}
        onArchive={() => void handleArchiveCohort()}
        onReactivate={() => void handleReactivateCohort()}
        onDelete={cohortStatus === "archived" ? () => void handleDeleteCohort() : undefined}
      />

      {cohortId && courseId ? (
        <>
          <AddContentModal
            open={addContentOpen}
            onClose={() => setAddContentOpen(false)}
            onChooseLesson={() => {
              setAddContentOpen(false);
              setAddLessonOpen(true);
            }}
            onChooseQuiz={() => {
              setAddContentOpen(false);
              setAddQuizOpen(true);
            }}
            onChoosePresentation={() => {
              setAddContentOpen(false);
              setAddPresentationOpen(true);
            }}
          />
          <AddLessonModal
            open={addLessonOpen}
            context="cohort"
            cohortId={cohortId}
            courseId={courseId}
            insertAfterId={insertAfterAssignmentId}
            sequenceLessons={lessons}
            assignments={assignments}
            onClose={() => setAddLessonOpen(false)}
            onAdded={() => refresh({ silent: true })}
          />
          <AddQuizModal
            open={addQuizOpen}
            context="cohort"
            courseId={courseId}
            cohortId={cohortId}
            insertAfterPlanItemId={planItemIdAfterAssignment(insertAfterAssignmentId, planItems, assignments)}
            planItems={planItems}
            assignments={assignments}
            onClose={() => setAddQuizOpen(false)}
            onAdded={() => refresh({ silent: true })}
          />
          <AddPresentationModal
            open={addPresentationOpen}
            context="cohort"
            courseId={courseId}
            cohortId={cohortId}
            insertAfterPlanItemId={planItemIdAfterAssignment(insertAfterAssignmentId, planItems, assignments)}
            planItems={planItems}
            assignments={assignments}
            onClose={() => setAddPresentationOpen(false)}
            onAdded={() => refresh({ silent: true })}
          />
        </>
      ) : null}
      <Toast />
    </div>
  );
}
